const parseConfig = (component) => {
    const rawConfig = component.dataset.gameConfig;

    if (!rawConfig) {
        return null;
    }

    try {
        return JSON.parse(rawConfig);
    } catch (error) {
        return null;
    }
};

const createLetterPaletteMarkup = (letters) => letters.map((letter) => `
    <button class="crew-name-builder__palette-button" type="button" data-letter-id="${letter.id}" aria-label="Add letter ${letter.label}">
        <img src="${letter.image_url}" alt="" loading="lazy">
    </button>
`).join('');

const createBuilderContentMarkup = (items) => items.map((item) => {
    if (item.type === 'space') {
        return '<span class="crew-name-builder__space" aria-hidden="true"></span>';
    }

    if (item.type === 'break') {
        return '<span class="crew-name-builder__break" aria-hidden="true"></span>';
    }

    return `<img class="crew-name-builder__letter" src="${item.image_url}" alt="${item.label}" loading="lazy">`;
}).join('');

const renderShell = (app, state) => {
    app.innerHTML = `
        <div class="crew-name-builder">
            <div class="crew-name-builder__palette">
                ${createLetterPaletteMarkup(state.letters)}
            </div>

            <div class="crew-name-builder__canvas" data-crew-name-preview>
            </div>

            <div class="crew-name-builder__actions">
                <button class="crew-name-builder__action" type="button" data-crew-action="space">${state.labels.addSpace}</button>
                <button class="crew-name-builder__action" type="button" data-crew-action="break">${state.labels.return}</button>
                <button class="crew-name-builder__action" type="button" data-crew-action="delete">${state.labels.delete}</button>
                <button class="crew-name-builder__action is-primary" type="button" data-crew-action="download">${state.labels.download}</button>
            </div>
        </div>
    `;
};

const renderPreview = (app, state) => {
    const preview = app.querySelector('[data-crew-name-preview]');
    const downloadButton = app.querySelector('[data-crew-action="download"]');
    const deleteButton = app.querySelector('[data-crew-action="delete"]');

    if (preview) {
        preview.innerHTML = createBuilderContentMarkup(state.items);
    }

    if (downloadButton) {
        downloadButton.disabled = !state.items.length;
    }

    if (deleteButton) {
        deleteButton.disabled = !state.items.length;
    }
};

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
});

const buildRows = (items) => {
    const rows = [[]];

    items.forEach((item) => {
        if (item.type === 'break') {
            rows.push([]);
            return;
        }

        rows[rows.length - 1].push(item);
    });

    return rows.filter((row, index) => row.length || index === 0);
};

const downloadPng = async (state) => {
    const rows = buildRows(state.items);
    const letterHeight = 100;
    const spaceWidth = 44;
    const rowGap = 24;
    const padding = 24;

    const imageCache = {};

    await Promise.all(
        state.items
            .filter((item) => item.type === 'letter')
            .map(async (item) => {
                if (!imageCache[item.image_url]) {
                    imageCache[item.image_url] = await loadImage(item.image_url);
                }
            })
    );

    const rowWidths = rows.map((row) => row.reduce((width, item) => {
        if (item.type === 'space') {
            return width + spaceWidth;
        }

        if (item.type === 'letter') {
            const image = imageCache[item.image_url];
            const ratio = image.naturalWidth / image.naturalHeight;

            return width + (letterHeight * ratio);
        }

        return width;
    }, 0));

    const contentWidth = Math.max(...rowWidths, 240);
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(contentWidth + (padding * 2));
    canvas.height = Math.ceil((rows.length * letterHeight) + ((rows.length - 1) * rowGap) + (padding * 2));

    const context = canvas.getContext('2d');

    if (!context) {
        return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    rows.forEach((row, rowIndex) => {
        let cursorX = padding;
        const cursorY = padding + (rowIndex * (letterHeight + rowGap));

        row.forEach((item) => {
            if (item.type === 'space') {
                cursorX += spaceWidth;
                return;
            }

            const image = imageCache[item.image_url];
            const ratio = image.naturalWidth / image.naturalHeight;
            const drawWidth = letterHeight * ratio;

            context.drawImage(image, cursorX, cursorY, drawWidth, letterHeight);
            cursorX += drawWidth;
        });
    });

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = state.downloadFileName;
    link.click();

    if (typeof window.ga === 'function') {
        window.ga('send', 'event', 'Co Crew Name', 'Click', 'Download Crew Name Download');
    }
};

const attachEvents = (component, app, state) => {
    app.addEventListener('click', async (event) => {
        const target = event.target.closest('[data-letter-id], [data-crew-action]');

        if (!target) {
            return;
        }

        if (target.dataset.letterId) {
            const letter = state.lettersById[Number(target.dataset.letterId)];

            if (!letter) {
                return;
            }

            state.items.push({
                type: 'letter',
                id: letter.id,
                label: letter.label,
                image_url: letter.image_url,
            });

            renderPreview(app, state);
            return;
        }

        const action = target.dataset.crewAction;

        if (action === 'space') {
            state.items.push({ type: 'space' });
            renderPreview(app, state);
            return;
        }

        if (action === 'break') {
            state.items.push({ type: 'break' });
            renderPreview(app, state);
            return;
        }

        if (action === 'delete') {
            state.items.pop();
            renderPreview(app, state);
            return;
        }

        if (action === 'download' && state.items.length) {
            await downloadPng(state);
        }
    });
};

const initCreateCrewName = () => {
    const components = document.querySelectorAll('[data-crew-name-component]');

    if (!components.length) {
        return;
    }

    components.forEach((component) => {
        const app = component.querySelector('[data-crew-name-app]');
        const config = parseConfig(component);

        if (!app || !config) {
            return;
        }

        const state = {
            letters: config.letters || [],
            lettersById: Object.fromEntries((config.letters || []).map((letter) => [letter.id, letter])),
            labels: config.labels || {},
            downloadFileName: config.downloadFileName || 'my-co-crew-name.png',
            items: [],
        };

        renderShell(app, state);
        renderPreview(app, state);
        attachEvents(component, app, state);
    });
};

export default initCreateCrewName;
